import type { Variant } from '~/lib/data-contract/salesforce';
import type { ProductEventData } from '~/shared/hooks/useEvents/models';

/**
 * Every label Salesforce puts on a buy button. The first five double as the actions the product
 * handler can dispatch, the rest only ever describe a button - a product that cannot be bought
 * still has to say why.
 */
export type ProductEventTypes =
    | 'addtocart'
    | 'clickandreserve'
    | 'outofstock'
    | 'findinstore'
    | 'navigateto'
    | 'comingsoon'
    | 'comingsoononline'
    | 'outofstockpermanently'
    | 'outofstockpermanentlyonline';

export type ProductCartInfo = Variant;

export type AddToCartEvent = {
    type: 'addtocart';
    payload: ProductCartInfo;
    productEventData: ProductEventData;
    quantity: number;
};

export type ClickAndReserve = {
    type: 'clickandreserve';
};

export type OutOfStock = {
    type: 'outofstock';
};

export type ReserveInStore = {
    type: 'findinstore';
    payload: {
        url: string;
    };
};

export type NavigateTo = {
    type: 'navigateto';
    payload: {
        url: string;
    };
};

/**
 * Only the types that carry an action. Deliberately narrower than `ProductEventTypes`: the
 * remaining labels have nothing to dispatch.
 */
export type ProductEventPayload =
    | AddToCartEvent
    | ClickAndReserve
    | OutOfStock
    | ReserveInStore
    | NavigateTo;

export type ProductEvent = ProductEventPayload & {
    type: ProductEventTypes;
};

/** Derived from the payloads, so it cannot drift from them. */
const dispatchableProductEvents: readonly ProductEventPayload['type'][] = [
    'addtocart',
    'clickandreserve',
    'outofstock',
    'findinstore',
    'navigateto',
];

/**
 * A button label is not necessarily something to dispatch: a product that is coming soon or gone
 * for good still has a button, and clicking it has nothing to send.
 */
export const isDispatchableProductEvent = (
    type?: ProductEventTypes,
): type is ProductEventPayload['type'] =>
    !!type && dispatchableProductEvents.includes(type as ProductEventPayload['type']);
